import { useCallback, useRef } from 'react';
import SearchablePicker, { SearchablePickerOption } from '../../../organisms/SearchablePicker';
import { FinancialRepresentative, personApi } from '../../../../services/personApi';
import FinancialRepresentativesTable, { FinancialRepresentativesLoadState } from './FinancialRepresentativesTable';
import { toCandidateOptions } from './financialRepresentatives';

const FIELD_LABEL = 'Representantes Financeiros';
const SEARCH_PLACEHOLDER = 'Buscar por nome ou CPF...';
const MINIMUM_SEARCH_LENGTH = 3;
const EMPTY_SEARCH_MESSAGE = 'Nenhuma pessoa física com o perfil Representante Financeiro.';

interface Props {
    items: FinancialRepresentative[];
    loadState?: FinancialRepresentativesLoadState;
    readOnly?: boolean;
    errorMessage?: string;
    onAdd: (item: FinancialRepresentative) => void;
    onRemove: (individualUuid: string) => void;
}

const ignoreClear = () => undefined;

const searchCandidates = async (term: string): Promise<FinancialRepresentative[]> => {
    try {
        return await personApi.searchFinancialRepresentativeCandidates(term);
    } catch {
        return [];
    }
};

const findCandidate = (candidates: FinancialRepresentative[], option: SearchablePickerOption): FinancialRepresentative | undefined =>
    candidates.find((candidate) => candidate.individualUuid === option.id);

/**
 * "Representantes Financeiros" field of the legal person (AF-4128): searches individuals with the
 * FINANCIAL_REPRESENTATIVE profile by name or CPF and adds them to the list below. In `readOnly`
 * only the list is shown, without search and trash.
 */
const FinancialRepresentativesField = ({ items, loadState, readOnly = false, errorMessage, onAdd, onRemove }: Props) => {
    const candidatesRef = useRef<FinancialRepresentative[]>([]);

    const handleSearch = useCallback(async (term: string): Promise<SearchablePickerOption[]> => {
        const candidates = await searchCandidates(term);
        candidatesRef.current = candidates;
        return toCandidateOptions(candidates, items);
    }, [items]);

    const handleSelect = useCallback((option: SearchablePickerOption) => {
        const candidate = findCandidate(candidatesRef.current, option);
        if (candidate) onAdd(candidate);
    }, [onAdd]);

    return (
        <div>
            {readOnly ? (
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{FIELD_LABEL}</span>
            ) : (
                <div className="md:w-3/5">
                    <SearchablePicker
                        label={FIELD_LABEL}
                        placeholder={SEARCH_PLACEHOLDER}
                        minChars={MINIMUM_SEARCH_LENGTH}
                        floatingResults
                        emptyMessage={EMPTY_SEARCH_MESSAGE}
                        onSearch={handleSearch}
                        onSelect={handleSelect}
                        onClear={ignoreClear}
                    />
                </div>
            )}
            {errorMessage ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errorMessage}</p> : null}
            <div className="mt-2">
                <FinancialRepresentativesTable items={items} loadState={loadState} onRemove={readOnly ? undefined : onRemove} />
            </div>
        </div>
    );
};

export default FinancialRepresentativesField;
